// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import { escapeHtml } from './utils/format';

export interface GlossaryEntry { title: string; body: string }

export const GLOSSARY: Record<string, GlossaryEntry> = {
  'recovery-rate': {
    title: 'Resource recovery rate',
    body: 'The share of waste that is kept out of landfill — recycled, turned into energy, or reused — instead of being disposed of. Australia\'s national rate was about 63% in 2020–21; the National Waste Policy target is 80% by 2030.',
  },
  recycling: {
    title: 'Recycling',
    body: 'Reprocessing waste materials into new products — e.g. steel back into steel, glass into new bottles, paper into cardboard. The largest form of resource recovery.',
  },
  'energy-recovery': {
    title: 'Energy recovery',
    body: 'Recovering energy from waste — burning it in a waste-to-energy plant, or capturing methane (landfill gas) to generate electricity. Counts as recovery, not disposal.',
  },
  reuse: {
    title: 'Waste reuse',
    body: 'Using a waste material again for a useful purpose without reprocessing it — for example, using crushed concrete or fly ash as construction fill.',
  },
  disposal: {
    title: 'Disposal (landfill)',
    body: 'Waste sent to landfill or otherwise permanently disposed of, with no material or energy recovered. The outcome resource recovery is designed to avoid.',
  },
  'long-term-storage': {
    title: 'Long-term storage',
    body: 'Hazardous or contaminated waste held in secure long-term storage because it cannot yet be safely recycled or disposed of. A small category, counted as not recovered.',
  },
  'core-waste': {
    title: 'Core vs non-core waste',
    body: 'Core waste is the ordinary material Australia manages through its waste system. Non-core waste — mainly fly ash from power stations and biosolids from sewage — is reported separately because it is very large and managed differently. This site includes both, and labels which is which.',
  },
  msw: {
    title: 'Municipal solid waste (MSW)',
    body: 'The household stream — rubbish, recycling and garden organics collected from homes by councils, plus council-managed waste. The bins you fill yourself.',
  },
  ci: {
    title: 'Commercial & industrial (C&I)',
    body: 'Waste generated by businesses, offices, factories, shops and institutions — everything that isn\'t household or construction waste.',
  },
  cd: {
    title: 'Construction & demolition (C&D)',
    body: 'Waste from building, renovation and demolition — concrete, brick, timber, metal and soil. Heavy, and mostly recovered.',
  },
  tonne: {
    title: 'Tonne (t) · kilotonne (kt) · megatonne (Mt)',
    body: 'A tonne is 1,000 kilograms. A kilotonne (kt) is 1,000 tonnes; a megatonne (Mt) is one million tonnes. Australia generated about 75.8 Mt of waste in 2020–21 — roughly 3 tonnes per person.',
  },
  fate: {
    title: 'Fate',
    body: 'What ultimately happens to a tonne of waste: recycling, energy recovery, reuse, disposal to landfill, or long-term storage.',
  },
  stream: {
    title: 'Waste stream',
    body: 'Where the waste comes from: municipal/household (MSW), commercial & industrial (C&I), or construction & demolition (C&D).',
  },
};

/** Inline glossary link with a small info icon; opens the popover on click. */
export function glossaryTerm(term: string, text: string): string {
  const e = GLOSSARY[term];
  const label = escapeHtml(text);
  if (!e) return label;
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button" aria-label="${escapeHtml(e.title)}: ${escapeHtml(e.body)}">${label}<span class="info-icon" aria-hidden="true">i</span></span>`;
}
